import React, { useRef, useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Alert,
  Dimensions,
  Platform,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Svg, { Path } from 'react-native-svg';
import { PanGestureHandler, State } from 'react-native-gesture-handler';
import { Button } from '../ui/Button';
import theme from '../../theme';

interface SignatureCanvasProps {
  visible: boolean;
  onClose: () => void;
  onSave: (signature: string) => void;
  title?: string;
  placeholder?: string;
}

interface Point {
  x: number;
  y: number;
}

export const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
  visible,
  onClose,
  onSave,
  title = 'التوقيع / Signature',
  placeholder = 'وقع هنا / Sign here'
}) => {
  const [paths, setPaths] = useState<string[]>([]);
  const [currentPath, setCurrentPath] = useState<Point[]>([]);
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 200 });

  const screenData = Dimensions.get('window');
  const isTablet = Math.min(screenData.width, screenData.height) >= 768;
  const isLandscape = screenData.width > screenData.height;

  useEffect(() => {
    if (visible) {
      // Calculate optimal canvas size for the device
      const padding = 40;
      const availableWidth = screenData.width - padding * 2;
      const availableHeight = screenData.height - padding * 4; // Extra padding for modal content
      
      let canvasWidth = Math.min(availableWidth, isTablet ? 600 : 350);
      let canvasHeight = Math.min(availableHeight * 0.6, isTablet ? 300 : 200);
      
      // Ensure minimum viable size
      canvasWidth = Math.max(canvasWidth, 280);
      canvasHeight = Math.max(canvasHeight, 150);
      
      setCanvasSize({ width: canvasWidth, height: canvasHeight });
    }
  }, [visible, screenData, isTablet]);

  const handleGestureEvent = (event: any) => {
    const { x, y } = event.nativeEvent;
    
    // Ensure coordinates are within canvas bounds
    if (x >= 0 && y >= 0 && x <= canvasSize.width && y <= canvasSize.height) {
      setCurrentPath(prev => [...prev, { x, y }]);
    }
  };

  const handleGestureEnd = (event: any) => {
    if (event.nativeEvent.state === State.END) {
      if (currentPath.length > 1) {
        // Convert path to SVG path string
        const pathString = currentPath.reduce((acc, point, index) => {
          if (index === 0) {
            return `M${point.x},${point.y}`;
          } else {
            return `${acc} L${point.x},${point.y}`;
          }
        }, '');
        
        setPaths(prev => [...prev, pathString]);
      }
      setCurrentPath([]);
    }
  };

  const clearSignature = () => {
    setPaths([]);
    setCurrentPath([]);
  };

  const saveSignature = async () => {
    if (paths.length === 0) {
      Alert.alert(
        'تحذير / Warning', 
        'لم يتم العثور على توقيع. يرجى التوقيع أولاً.\nNo signature found. Please sign first.'
      );
      return;
    }

    try {
      // Create SVG string with all paths
      const svgPaths = paths.map(path => `<path d="${path}" stroke="#000" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />`).join('');
      const svgString = `<svg width="${canvasSize.width}" height="${canvasSize.height}" xmlns="http://www.w3.org/2000/svg">${svgPaths}</svg>`;
      
      // Convert SVG to base64 for storage
      const base64Signature = `data:image/svg+xml;base64,${btoa(svgString)}`;
      
      onSave(base64Signature);
      onClose();
      clearSignature();
    } catch (error) {
      Alert.alert('خطأ / Error', 'فشل في حفظ التوقيع / Failed to save signature');
    }
  };

  const renderCurrentPath = () => {
    if (currentPath.length < 2) return null;
    
    const pathString = currentPath.reduce((acc, point, index) => {
      if (index === 0) {
        return `M${point.x},${point.y}`;
      } else {
        return `${acc} L${point.x},${point.y}`;
      }
    }, '');

    return (
      <Path
        d={pathString}
        stroke="#000"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { 
          width: canvasSize.width + 80,
          maxWidth: screenData.width - 20 
        }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>{title}</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Canvas Container */}
          <View style={styles.canvasContainer}>
            <Text style={styles.placeholder}>{placeholder}</Text>
            
            <PanGestureHandler
              onGestureEvent={handleGestureEvent}
              onHandlerStateChange={handleGestureEnd}
              shouldCancelWhenOutside={true}
            >
              <View style={[styles.canvas, { 
                width: canvasSize.width, 
                height: canvasSize.height 
              }]}>
                <Svg 
                  width={canvasSize.width} 
                  height={canvasSize.height}
                  style={StyleSheet.absoluteFillObject}
                >
                  {/* Render saved paths */}
                  {paths.map((path, index) => (
                    <Path
                      key={index}
                      d={path}
                      stroke="#000"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ))}
                  
                  {/* Render current path being drawn */}
                  {renderCurrentPath()}
                </Svg>
                
                {/* Signature guidelines */}
                <View style={styles.guidelines}>
                  <View style={styles.guideline} />
                </View>
              </View>
            </PanGestureHandler>
          </View>

          {/* Instructions */}
          <View style={styles.instructions}>
            <Text style={styles.instructionText}>
              {isTablet 
                ? 'استخدم إصبعك أو القلم للتوقيع / Use your finger or stylus to sign'
                : 'استخدم إصبعك للتوقيع / Use your finger to sign'
              }
            </Text>
          </View>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <Button
              title="مسح / Clear"
              onPress={clearSignature}
              variant="outline"
              style={styles.button}
              disabled={paths.length === 0}
            />
            <Button
              title="حفظ / Save"
              onPress={saveSignature}
              variant="primary"
              style={styles.button}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

// Signature Display Component for showing saved signatures
interface SignatureDisplayProps {
  signature: string | null;
  onEdit: () => void;
  placeholder?: string;
  style?: any;
  readOnly?: boolean; // Add read-only prop
}

export const SignatureDisplay: React.FC<SignatureDisplayProps> = ({
  signature,
  onEdit,
  placeholder = 'اضغط للتوقيع / Tap to Sign',
  style,
  readOnly = false
}) => {
  const Component = readOnly ? View : TouchableOpacity;
  
  return (
    <Component 
      style={[styles.signatureDisplay, style]} 
      onPress={readOnly ? undefined : onEdit}
    >
      {signature ? (
        <View style={styles.signatureContainer}>
          {/* For web, we can display the SVG directly */}
          {Platform.OS === 'web' ? (
            <img 
              src={signature} 
              alt="Signature" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          ) : (
            // For native, we'd need to use react-native-svg or similar
            <Text style={styles.signatureText}>التوقيع محفوظ / Signature Saved</Text>
          )}
          {!readOnly && (
            <TouchableOpacity style={styles.editButton} onPress={onEdit}>
              <Ionicons name="pencil" size={16} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <View style={styles.placeholderContainer}>
          {!readOnly && <Ionicons name="create" size={24} color={theme.colors.textSecondary} />}
          <Text style={styles.placeholderText}>
            {readOnly ? 'لا يوجد توقيع / No Signature' : placeholder}
          </Text>
        </View>
      )}
    </Component>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
  modalContent: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: theme.spacing.lg,
    maxHeight: '90%',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
  },
  title: {
    fontSize: 18,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  closeButton: {
    padding: theme.spacing.xs,
  },
  canvasContainer: {
    marginBottom: theme.spacing.md,
  },
  placeholder: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  canvas: {
    borderWidth: 2,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    backgroundColor: theme.colors.white,
    position: 'relative',
  },
  guidelines: {
    position: 'absolute',
    bottom: '30%',
    left: '10%',
    right: '10%',
    height: 1,
  },
  guideline: {
    height: 1,
    backgroundColor: theme.colors.gray200,
    opacity: 0.5,
  },
  instructions: {
    marginBottom: theme.spacing.lg,
  },
  instructionText: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  button: {
    flex: 1,
  },
  signatureDisplay: {
    height: 80,
    borderWidth: 2,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    backgroundColor: theme.colors.gray50,
    position: 'relative',
  },
  signatureContainer: {
    flex: 1,
    padding: theme.spacing.sm,
    position: 'relative',
  },
  signatureText: {
    color: theme.colors.textSecondary,
    fontSize: 12,
    textAlign: 'center',
    marginTop: theme.spacing.md,
  },
  editButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 24,
    height: 24,
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
  },
  placeholderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  placeholderText: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
});